/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog } from '../../Common/AppDialog'
import { testingOverrideAllowsAllFeatures } from '../../../config/testingOverrides'

export interface SessionLeaseContextValue {
  remainingMinutes: number | null
  isStale: boolean
  triggerSessionStale: () => void
  refreshSession: () => Promise<void>
  recordActivity: () => void
}

export const SessionLeaseContext = createContext<SessionLeaseContextValue>({
  remainingMinutes: null,
  isStale: false,
  triggerSessionStale: () => {},
  refreshSession: async () => {},
  recordActivity: () => {},
})

export interface SessionLeaseProviderProps {
  authTime?: number | null // in seconds since epoch
  initialActivityAt?: number | null // in milliseconds since epoch
  leaseMinutes?: number
  onReauth?: () => Promise<void>
  onSignOut?: () => Promise<void>
  children: ReactNode
}

export function SessionLeaseProvider({
  authTime,
  initialActivityAt,
  leaseMinutes = 30,
  onReauth,
  onSignOut,
  children,
}: SessionLeaseProviderProps) {
  const { t } = useNls()
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null)
  const [isStaleModalOpen, setIsStaleModalOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const computeInitialActivity = useCallback(() => {
    if (typeof initialActivityAt === 'number' && initialActivityAt > 0) {
      return initialActivityAt
    }
    return Date.now()
  }, [initialActivityAt])


  const lastActivityAtRef = useRef<number>(initialActivityAt && initialActivityAt > 0 ? initialActivityAt : 0)
  const lastThrottleRef = useRef<number>(0)

  useEffect(() => {
    lastActivityAtRef.current = computeInitialActivity()
  }, [computeInitialActivity])

  const recordActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastThrottleRef.current > 1000) {
      lastThrottleRef.current = now
      lastActivityAtRef.current = now
    }
  }, [])

  // Listen to user interaction to reset inactivity timer
  useEffect(() => {
    const handleActivity = () => {
      recordActivity()
    }
    window.addEventListener('pointerdown', handleActivity, { passive: true })
    window.addEventListener('keydown', handleActivity, { passive: true })
    window.addEventListener('touchstart', handleActivity, { passive: true })
    window.addEventListener('scroll', handleActivity, { passive: true })

    return () => {
      window.removeEventListener('pointerdown', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('touchstart', handleActivity)
      window.removeEventListener('scroll', handleActivity)
    }
  }, [recordActivity])

  const triggerSessionStale = useCallback(() => {
    setIsStaleModalOpen(true)
  }, [])

  const refreshSession = useCallback(async () => {
    setIsRefreshing(true)
    try {
      if (onReauth) {
        await onReauth()
      }
      lastActivityAtRef.current = Date.now()
      setIsStaleModalOpen(false)
    } catch (err) {
      console.error('[SessionLeaseProvider] session refresh failed', err)
    } finally {
      setIsRefreshing(false)
    }
  }, [onReauth])

  // Periodic lease checker based on inactivity (lastActivityAt) and outer 24h bound
  useEffect(() => {
    const checkLease = () => {
      if (testingOverrideAllowsAllFeatures()) {
        setRemainingMinutes(leaseMinutes)
        return
      }

      const nowMs = Date.now()
      const nowSec = Math.floor(nowMs / 1000)

      // Outer bound: reject tokens older than 24h regardless of activity
      if (authTime && (nowSec - authTime) > 24 * 3600) {
        setRemainingMinutes(0)
        setIsStaleModalOpen(true)
        return
      }

      const elapsedMs = nowMs - lastActivityAtRef.current
      const leaseMs = leaseMinutes * 60 * 1000
      const remainingMs = leaseMs - elapsedMs
      const remMin = Math.max(0, Math.floor(remainingMs / 60000))
      setRemainingMinutes(remMin)

      if (remainingMs <= 0) {
        setIsStaleModalOpen(true)
      }
    }

    checkLease()
    const timer = setInterval(checkLease, 5000)
    return () => clearInterval(timer)
  }, [authTime, leaseMinutes])

  return (
    <SessionLeaseContext.Provider
      value={{
        remainingMinutes,
        isStale: isStaleModalOpen,
        triggerSessionStale,
        refreshSession,
        recordActivity,
      }}
    >
      {children}

      <AppDialog
        open={isStaleModalOpen}
        title={t('NLSOwnerConsoleSessionStale')}
        message={
          <p style={{ margin: '8px 0', fontSize: 14, color: 'var(--text-mid)' }}>
            {t('NLSOwnerConsoleSessionStalePrompt')}
          </p>
        }
        actions={[
          ...(onSignOut
            ? [
                {
                  label: t('NLSOwnerConsoleSignOut'),
                  variant: 'secondary' as const,
                  onClick: async () => {
                    setIsStaleModalOpen(false)
                    await onSignOut()
                  },
                },
              ]
            : []),
          {
            label: isRefreshing ? t('NLSOwnerConsoleLoadingData') : t('NLSOwnerConsoleSessionRefresh'),
            variant: 'primary' as const,
            disabled: isRefreshing,
            loading: isRefreshing,
            onClick: async () => {
              await refreshSession()
            },
          },
        ]}
      />
    </SessionLeaseContext.Provider>
  )
}

export function useSessionLease(): SessionLeaseContextValue {
  return useContext(SessionLeaseContext)
}

export const isSessionStaleError = (error: unknown): boolean => {
  if (!error) return false
  if (typeof error === 'object') {
    const err = error as Record<string, unknown>
    if (err.code === 'owner-console/session-stale' || err.code === 'functions/owner-console/session-stale') return true
    if (typeof err.message === 'string' && (err.message.includes('owner-console/session-stale') || err.message.includes('Session lease expired'))) return true
    if (err.details && typeof err.details === 'object') {
      const details = err.details as Record<string, unknown>
      if (details.code === 'owner-console/session-stale') return true
    }
  }
  return false
}
